import React, { FormEvent, PropsWithChildren } from 'react';
import { RouteComponentProps } from 'react-router';
import { AppState } from '../../../../store/store.types';
import { ThunkDispatch } from 'redux-thunk';
import {
  BackendIntegrationAuthenticationType,
  BackendIntegrationAuthenticationTypeOptions,
  BackendIntegrationType,
  BackendIntegrationTypeOptions,
  SupplierFullSyncStatus,
  supplierFullSyncRoles,
  SupplierInfoModel,
  SupplierRequest
} from '../../../../api/core/core.types';
import { connect } from 'react-redux';
import {
  editSupplier,
  getSupplierFullSyncStatus,
  getSupplierInfo,
  triggerSupplierFullSync
} from '../../actions/supplierinfo.actions';
import { SupplierInfoActionTypes } from '../../types/supplierinfo.types';
import { FieldControl, FieldGroup, FormArray, FormBuilder, Validators } from 'react-reactive-form';
import { adminModuleEditRoles, SupplierModel } from '../../../../api/admin/admin.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import Checkbox from '../../../../components/forms/Checkbox';
import SectionTitle from '../../../../components/forms/SectionTitle';
import { WithTranslation, withTranslation } from 'react-i18next';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { InputTextArea } from '../../../../components/forms/TextArea';
import Select from '../../../../components/forms/Select';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '../../../../api/api.types';

interface SupplierProps extends RouteComponentProps, WithTranslation {
  supplier: SupplierModel | null;
  user: UserModel | null;
  supplierInfo: SupplierInfoModel | null;
  getSupplier: (code: string) => void;
  editSupplier: (code: string, request: SupplierRequest) => Promise<void>;
  triggerSupplierFullSync: (code: string) => Promise<void>;
  getFullSyncStatus: (code: string) => Promise<SupplierFullSyncStatus>;
}

interface SupplierState {
  backendIntegrationType: { value: BackendIntegrationType, label: string } | undefined;
  backendIntegrationAuthenticationType: { value: BackendIntegrationAuthenticationType, label: string } | undefined;
  backendIntegrationUsername: string;
  backendIntegrationPassword: string;
  backendIntegrationApiKey: string;
  backendIntegrationUrl: string;
  fullSyncModalOpen: boolean;
  fullSyncInProgress: boolean;
  fullSyncStatus: SupplierFullSyncStatus | null;
}

class Supplier extends React.Component<PropsWithChildren<SupplierProps>, SupplierState> {

  constructor(props: SupplierProps) {
    super(props);
    this.state = {
      backendIntegrationType: undefined,
      backendIntegrationAuthenticationType: undefined,
      backendIntegrationUsername: '',
      backendIntegrationPassword: '',
      backendIntegrationApiKey: '',
      backendIntegrationUrl: '',
      fullSyncModalOpen: false,
      fullSyncInProgress: false,
      fullSyncStatus: null
    };
  }

  form = FormBuilder.group({
    maxRentalDuration: [ null, Validators.required ],
    gracePeriodHours: [ null, Validators.required ],
    minTimeToPickup: [ null ],
    minTimeToCancel: [ null ],
    maxDriverAge: [ null ],
    reservationsEmailTo: FormBuilder.array([]),
    reservationsEmailBcc: [ '' ],
    customerEmailApiKey: [ '' ],
    customerEmailFrom: [ '' ],
    sendConfirmed: [ false ],
    sendPending: [ false ],
    sendCancellation: [ false ],
    sendBackendCancellationFailed: [ false ],
    sendBackendCreationFailed: [ false ],
    sendReservationAsAttachment: [ false ],
    hasBookingWeb: [ false ],
    tableauEmail: [ null ],
    tableauBillingUrl: [ null ],
    tableauInsightsUrl: [ null ],
    username: [ '' ],
    password: [ '' ],
    url: [ '' ],
    apiKey: [ '' ]
  });


  componentDidMount() {
    const id = this.props.supplier?.code;
    if (id) {
      this.props.getSupplier(id);
      this.loadFullSyncStatus(id);
    }
  }

  canTriggerFullSync() {
    return !!this.props.user && userCanAccessComponent(this.props.user, supplierFullSyncRoles);
  }

  loadFullSyncStatus = async (code: string) => {
    if (!this.canTriggerFullSync()) {
      return;
    }

    try {
      const status = await this.props.getFullSyncStatus(code);
      this.setState({ fullSyncStatus: status });
    } catch (e) {
      this.setState({ fullSyncStatus: null });
    }
  };

  toggleFullSyncModal = () => {
    this.setState({ fullSyncModalOpen: !this.state.fullSyncModalOpen });
  };

  onConfirmFullSync = async () => {
    const code = this.props.supplier?.code;
    this.setState({ fullSyncModalOpen: false });

    if (!code || this.state.fullSyncInProgress) {
      return;
    }

    this.setState({ fullSyncInProgress: true });

    try {
      await this.props.triggerSupplierFullSync(code);
      toast(`Full sync started for ${this.props.supplier?.name ?? code}`, { type: 'success', hideProgressBar: true });
      await this.loadFullSyncStatus(code);
    } finally {
      this.setState({ fullSyncInProgress: false });
    }
  };

  getFullSyncStatusText() {
    const status = this.state.fullSyncStatus;

    if (!status || (!status.startedAt && !status.finishedAt)) {
      return 'Never synced';
    }

    if (status.inProgress) {
      return `Sync in progress, started ${dayjs(status.startedAt).format(DATE_TIME_FORMAT)}`;
    }

    return `Last sync ${dayjs(status.startedAt).format(DATE_TIME_FORMAT)}`
      + ` - ${dayjs(status.finishedAt).format(DATE_TIME_FORMAT)}`;
  }

  componentDidUpdate(prevProps: SupplierProps) {
    if (this.props.supplierInfo && (!prevProps.supplierInfo || this.props.supplierInfo !== prevProps.supplierInfo)) {
      this.updateFormValue();
    }
  }

  updateFormValue = () => {
    if (!this.props.supplierInfo) {
      return;
    }

    const supplier = this.props.supplierInfo;
    const additionalData = supplier.additionalData;

    // this.form.reset();

    if (supplier) {
      this.form.patchValue({
        maxRentalDuration: supplier.maxRentalDuration,
        gracePeriodHours: supplier.gracePeriodHours,
        minTimeToPickup: supplier.minTimeToPickup,
        minTimeToCancel: supplier.minTimeToCancel,
        maxDriverAge: supplier.maxDriverAge,
        hasBookingWeb: supplier.hasBookingWeb,
        tableauEmail: supplier.tableauEmail,
        tableauInsightsUrl: supplier.tableauInsightsUrl,
        tableauBillingUrl: supplier.tableauBillingUrl,
        reservationsEmailTo: [],
        reservationsEmailBcc: additionalData?.reservationsEmailBcc,
        customerEmailApiKey: additionalData?.customerEmailApiKey,
        customerEmailFrom: additionalData?.customerEmailFrom,
        sendConfirmed: additionalData?.sendConfirmed,
        sendPending: additionalData?.sendPending,
        sendCancellation: additionalData?.sendCancellation,
        sendBackendCancellationFailed: additionalData?.sendBackendCancellationFailed,
        sendBackendCreationFailed: additionalData?.sendBackendCreationFailed,
        sendReservationAsAttachment: additionalData?.sendReservationAsAttachment,
        backendIntegrationApiKey: supplier.backendIntegration?.apiKey ?? '',
        backendIntegrationUsername: supplier.backendIntegration?.username ?? '',
        backendIntegrationPassword: supplier.backendIntegration?.password ?? '',
        backendIntegrationUrl: supplier.backendIntegration?.url ?? '',
        backendIntegrationAuthenticationType: BackendIntegrationAuthenticationTypeOptions.find(auth => auth.value === supplier.backendIntegration?.authenticationType),
        backendIntegrationType: BackendIntegrationTypeOptions.find(type => type.value === supplier.backendIntegration?.backendIntegrationType)

      });

      this.setState({
        backendIntegrationAuthenticationType: BackendIntegrationAuthenticationTypeOptions.find(auth => auth.value === supplier.backendIntegration?.authenticationType),
        backendIntegrationType: BackendIntegrationTypeOptions.find(type => type.value === supplier.backendIntegration?.backendIntegrationType)
      });
    }

    this.form.controls.reservationsEmailTo.reset();
    additionalData?.reservationsEmailTo!.forEach((e) => {
      if (e && e.length) {
        this.stringArrayField.push(FormBuilder.control(e));
      }
    });
  };

  onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();

    if (this.form.invalid || !this.props.supplier?.code) {
      return;
    }

    const reservationEmailToAddresses = [];
    for (const em of form.reservationsEmailTo.value) {
      if (em && em.length) {
        reservationEmailToAddresses.push(em);
      }
    }

    const request: SupplierRequest = {
      maxRentalDuration: form.maxRentalDuration.value,
      gracePeriodHours: form.gracePeriodHours.value,
      minTimeToPickup: form.minTimeToPickup.value,
      minTimeToCancel: form.minTimeToCancel.value,
      maxDriverAge: form.maxDriverAge.value,
      tableauEmail: form.tableauEmail.value,
      tableauInsightsUrl: form.tableauInsightsUrl.value,
      tableauBillingUrl: form.tableauBillingUrl.value,
      additionalData: {
        reservationsEmailTo: reservationEmailToAddresses,
        reservationsEmailBcc: form.reservationsEmailBcc.value,
        customerEmailApiKey: form.customerEmailApiKey.value,
        customerEmailFrom: form.customerEmailFrom.value,
        sendConfirmed: form.sendConfirmed.value,
        sendPending: form.sendPending.value,
        sendCancellation: form.sendCancellation.value,
        sendBackendCancellationFailed: form.sendBackendCancellationFailed.value,
        sendBackendCreationFailed: form.sendBackendCreationFailed.value,
        sendReservationAsAttachment: form.sendReservationAsAttachment.value
      },
      backendIntegration: {
        backendIntegrationType: form.backendIntegrationType?.value?.value,
        apiKey: form.backendIntegrationApiKey?.value,
        username: form.backendIntegrationUsername?.value,
        password: form.backendIntegrationPassword?.value,
        authenticationType: form.backendIntegrationAuthenticationType?.value?.value,
        url: form.backendIntegrationUrl?.value
      }
    };

    await this.props.editSupplier(this.props.supplier.code, request);
    this.props.getSupplier(this.props.supplier.code);
  };

  onCancel = () => {
    this.updateFormValue();
  };


  generateArrayList() {
    const { t } = this.props;

    return <div className="form-group d-flex">
      <div className="col-4 d-flex align-items-start justify-content-end">
        <label>{t('l.configuration.reservations_email_to')}: </label>
      </div>
      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input flex-column flex-wrap">
        {this.stringArrayField.controls.map((c, inx) => {
          if (c.value != null) {
            return <div key={inx} className="d-flex w-100 justify-content-center"><input
              className={'flex-grow-1'}
              value={c.value} onChange={(v) => c.setValue(v.target.value)}/>
              <button className={'flex-grow-1 btn-danger'} onClick={(e) => {
                e.preventDefault();
                this.removeStringFromArray(inx);
              }
              }>{t('b.configuration.delete')}</button>
            </div>;
          }
        })}
        <button className={'mt-1 mb-1'} onClick={(e) => {
          e.preventDefault();
          this.addStringToArray('');
        }}>{t('b.configuration.add_to_email')}</button>
      </div>
    </div>;
  }


  get stringArrayField(): FormArray {
    return this.form.get('reservationsEmailTo') as FormArray;
  }

  addStringToArray(str: string) {
    this.stringArrayField.push(FormBuilder.control(str));
  }

  removeStringFromArray(index: number) {
    this.stringArrayField.removeAt(index);
  }

  backendIntegrationTypeDropdownChange = async (newValue: any) => {
    await this.promisedSetState({ backendIntegrationType: newValue });

    if (newValue.length === 0) {
      await this.promisedSetState({ backendIntegrationAuthenticationType: undefined });
    }

    this.form.patchValue({
      backendIntegrationType: newValue
    });
  };

  backendIntegrationAuthTypeDropdownChange = async (newValue: any) => {
    await this.promisedSetState({ ...this.state, backendIntegrationAuthenticationType: newValue });
    this.form.patchValue({
      backendIntegrationAuthenticationType: newValue
    });
  };


  promisedSetState = (newState: any) => new Promise((resolve: any) => this.setState(newState, resolve));

  render() {
    const { t } = this.props;
    const disabled: boolean = !(this.props.user && userCanAccessComponent(this.props.user, adminModuleEditRoles));
    const canTriggerFullSync: boolean = this.canTriggerFullSync();
    const showUsernameField = (this.state.backendIntegrationType?.value === BackendIntegrationType.BLUEBIRD ||
      this.state.backendIntegrationAuthenticationType?.value === BackendIntegrationAuthenticationType.BASIC_AUTH) && this.state.backendIntegrationType?.value !== BackendIntegrationType.ANNATA;

    const showApiKeyField = this.state.backendIntegrationType?.value === BackendIntegrationType.WEBHOOK &&
      this.state.backendIntegrationAuthenticationType?.value === BackendIntegrationAuthenticationType.API_KEY;

    return (
      <>
        {this.props.children}
        <main>
          <div className="page-content">
            <FieldGroup
              strict={false}
              control={this.form}
              render={() => (
                <form className="form-horizontal pt-1" onSubmit={(e) => this.onSubmit(e)}>
                  <div className="col-12">
                    {canTriggerFullSync ? (
                      <>
                        <SectionTitle
                          title="MAIN CONFIGURATION"
                          showButton
                          buttonName={this.state.fullSyncInProgress ? 'STARTING FULL SYNC...' : 'START FULL SYNC'}
                          onEditClick={this.toggleFullSyncModal}
                        />
                        <div className="d-flex justify-content-end">
                          <div className={this.state.fullSyncStatus?.inProgress ? 'text-warning' : 'info-text'}>
                            {this.getFullSyncStatusText()}
                          </div>
                        </div>
                      </>
                    ) : <SectionTitle title="MAIN CONFIGURATION"/>}
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.name')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        {this.props.supplier?.name}
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.code')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        {this.props.supplier?.code}
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.max_rental_duration')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="maxRentalDuration"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.grace_period_hours')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="gracePeriodHours"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.min_time_to_pickup')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="minTimeToPickup"
                          render={NumberInput}

                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.min_time_to_cancel')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="minTimeToCancel"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.max_driver_age')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="maxDriverAge"
                          render={NumberInput}
                        />
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.configuration.additional_data').toUpperCase()}/>
                    {this.generateArrayList()}
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.reservations_email_bcc')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="reservationsEmailBcc"
                          render={TextInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.reservations_email_apikey')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="customerEmailApiKey"
                          render={TextInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.customer_email_from')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="customerEmailFrom"
                          render={TextInput}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex justify-content-end align-items-center">
                        {t('l.configuration.send_confirmed')}
                      </div>
                      <div className="col-2 d-flex align-items-center">
                        <FieldControl
                          name="sendConfirmed"
                          render={(props) =>
                            <Checkbox {...props} id="sendConfirmed" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.send_pending')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="sendPending"
                          render={(props) =>
                            <Checkbox {...props} id="sendPending" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.send_cancellation')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="sendCancellation"
                          render={(props) =>
                            <Checkbox {...props} id="sendCancellation" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.send_cancellation_failed')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="sendBackendCancellationFailed"
                          render={(props) =>
                            <Checkbox {...props} id="sendBackendCancellationFailed" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.send_creation_failed')}: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="sendBackendCreationFailed"
                          render={(props) =>
                            <Checkbox {...props} id="sendBackendCreationFailed" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>Send reservation as attachment: </label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="sendReservationAsAttachment"
                          render={(props) =>
                            <Checkbox {...props} id="sendReservationAsAttachment" className="mt-1"></Checkbox>}
                        />
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.configuration.backend_integration').toUpperCase()}/>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.backend_integration_type')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          strict={false}
                          name="backendIntegrationType"
                          render={(props) => (
                            <Select
                              value={this.state.backendIntegrationType}
                              className="mr-1"
                              placeholder="None"
                              options={BackendIntegrationTypeOptions}
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              label="backend integration type"
                              zIndex={10000}
                              isDisabled={disabled}
                              onChange={(value) => this.backendIntegrationTypeDropdownChange(value)}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <FieldControl
                        strict={false}
                        name="backendIntegrationAuthenticationType"
                        render={(props) => this.state.backendIntegrationType?.value === BackendIntegrationType.WEBHOOK ? (
                          <>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                              <label>Authentication type:</label>
                            </div>
                            <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                              <Select
                                value={this.state.backendIntegrationAuthenticationType}
                                className="mr-1"
                                placeholder="None"
                                options={BackendIntegrationAuthenticationTypeOptions}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                zIndex={9999}
                                isDisabled={disabled}
                                label="backend integration authentication type"
                                onChange={(value) => this.backendIntegrationAuthTypeDropdownChange(value)}
                              /></div>
                          </>

                        ) : <></>}
                      />
                    </div>
                    <div className="form-group d-flex">
                      <FieldControl
                        strict={false}
                        name="backendIntegrationUsername"
                        render={(props) => showUsernameField ? (<>
                          <div className="col-4 d-flex align-items-center justify-content-end">
                            <label>{t('l.configuration.username')}:
                            </label>
                          </div>
                          <div
                            className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                            <TextInput
                              {...props}
                              disabled={disabled}
                            /></div>
                        </>) : <></>
                        }
                      />
                    </div>
                    <div className="form-group d-flex">
                      <FieldControl
                        name="backendIntegrationPassword"
                        strict={false}
                        render={(props) => showUsernameField ? (<>
                          <div className="col-4 d-flex align-items-center justify-content-end">
                            <label>{t('l.configuration.password')}:
                            </label>
                          </div>
                          <div
                            className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                            <TextInput
                              {...props}
                              disabled={disabled}
                            /></div>
                        </>) : <></>
                        }
                      />
                    </div>
                    <div className="form-group d-flex">
                      <FieldControl
                        name="backendIntegrationApiKey"
                        strict={false}
                        render={(props) => showApiKeyField ? (
                          <>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                              <label>{t('l.configuration.apikey')}:</label>
                            </div>
                            <div
                              className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                              <TextInput
                                {...props}
                                disabled={disabled}
                              /></div>
                          </>
                        ) : <></>
                        }
                      />
                    </div>
                    <div className="form-group d-flex">
                      <FieldControl
                        name="backendIntegrationUrl"
                        strict={false}
                        render={(props) => this.state.backendIntegrationType?.value !== undefined ? (
                          <>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                              <label>{t('l.configuration.url')}:</label>
                            </div>
                            <div
                              className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                              <InputTextArea
                                {...props}
                                disabled={disabled}
                              />
                            </div>
                          </>) : <></>}
                      />
                    </div>
                    <SectionTitle title={t('section_title.configuration.booking_web_integration').toUpperCase()}/>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.has_booking_web')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="hasBookingWeb"
                          render={(props) =>
                            <Checkbox
                              {...props}
                              id="hasBookingWeb"
                              className="mt-1"
                              disabled
                            />}
                        />
                      </div>
                    </div>
                    <SectionTitle title={t('section_title.configuration.tableau_configuration').toUpperCase()}/>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.tableau_email')}:</label>
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <FieldControl
                          name="tableauEmail"
                          render={(props) =>
                            <TextInput
                              {...props}
                              disabled={disabled}/>
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.tableau_billing')}:</label>
                      </div>
                      <div
                        className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                        <FieldControl
                          name="tableauBillingUrl"
                          strict={false}
                          render={(props) =>
                            <InputTextArea
                              {...props}
                              disabled={disabled}
                            />
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group d-flex">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                        <label>{t('l.configuration.tableau_insights')}:</label>
                      </div>
                      <div
                        className="col-4 d-flex align-items-center justify-content-start input_two_items_wide padding-zero edit-form-row-input-margins">
                        <FieldControl
                          name="tableauInsightsUrl"
                          render={(props) =>
                            <InputTextArea
                              {...props}
                              disabled={disabled}
                            />
                          }
                        />
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-4 d-flex align-items-center justify-content-end">
                      </div>
                      <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                        <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                                onClick={this.onCancel}>{t('b.cancel_Generic cancel button translation')
                          .toUpperCase()}</button>
                        <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                          {t('b.save_Generic save button translation').toUpperCase()}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            />
          </div>
        </main>
        <ConfirmationModal
          isOpen={this.state.fullSyncModalOpen}
          toggle={this.toggleFullSyncModal}
          title="Start full sync"
          content={`This starts a full sync for ${this.props.supplier?.name ?? ''} and can take a long time. Continue?`}
          onConfirm={this.onConfirmFullSync}
          onCancel={this.toggleFullSyncModal}
        />
      </>
    )
      ;
  }
}

const mapStateToProps = (state: AppState) => ({
  supplier: state.authentication.activeSupplier,
  supplierInfo: state.setup.supplierInfo.supplierInfo,
  user: state.authentication.user
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, SupplierInfoActionTypes>) => ({
  getSupplier: () => dispatch(getSupplierInfo()),
  editSupplier: (code: string, request: SupplierRequest) => dispatch(editSupplier(code, request)),
  triggerSupplierFullSync: (code: string) => dispatch(triggerSupplierFullSync(code)),
  getFullSyncStatus: (code: string) => dispatch(getSupplierFullSyncStatus(code))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Supplier));
